import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Autocomplete, Box, TextField, Button, IconButton, Alert, Snackbar, Tooltip,
  FormControlLabel, Switch
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { Add, Delete, DragIndicator, Link as LinkIcon, Lock as LockIcon } from '@mui/icons-material';
import { markCatalogItemDestroyed, restoreCatalogItem } from '../../utils/catalogStatusApi';
import { WizardAccordion } from '../../components/WizardAccordion';
import { ImageThumbnail } from '../../components/ImageThumbnail';
import { RelatedWorkThumbnail } from '../../components/RelatedWorkThumbnail';
import { CVAutocomplete } from '../../components/CVAutocomplete';
import { FavoriteRowToggle } from '../../components/FavoriteRowToggle';
import { FavoritesRail } from '../../components/FavoritesRail';
import { AwardsSection, AwardField } from './AwardsSection';
import { en } from '../../translations/en';
/** Merge old per-section notes into a single 'notes' key (one-way migration). */
const mergeOldNotes = (stepNotes: Record<string, string[]>): Record<string, string[]> => {
  if (stepNotes.notes) return stepNotes;
  const oldKeys = Object.keys(stepNotes).filter(k => k !== 'notes');
  const merged = oldKeys.flatMap(k => stepNotes[k] || []).filter(Boolean);
  return merged.length > 0 ? { notes: merged } : {};
};
import { MarkdownLinkHelp } from '../../components/MarkdownLinkHelp';
import { isSafeHttpUrl, validateMarkdownLinks } from '../../utils/safeMarkdown';
import { useCatalogProvenance } from '../../hooks/useCatalogProvenance';
import { useCatalog } from '../../hooks/useCatalog';
import { useUpload } from '../../hooks/useUpload';
import { useWizardCompletion } from '../../hooks/useWizardCompletion';
import { useAutosaveStatus } from '../../hooks/useAutosaveStatus';
import { useCVSuggestions } from '../../hooks/useCVSuggestions';
import { useAuthorization } from '../../hooks/useAuthorization';
import { logger } from '../../utils/logger';
import { hasNonEmptyRelatedWork } from '../../utils/wizardCompletionCalculator';

/** Compile-time-checked accordion ids inside ProvenanceTab. */
export type ProvenanceSectionId =
  | 'provenance-records'
  | 'provenance-exhibitions'
  | 'provenance-publications'
  | 'provenance-awards'
  | 'provenance-related-works';

interface ProvenanceTabProps {
  catalogItemId?: string;
  ownerId?: string | null;
  /**
   * Optional whitelist of accordion ids to render. When undefined, every
   * section renders (preserves the classic /upload-object behavior). The
   * /edit-object redesign passes this to split ProvenanceTab across five
   * new top-level steps.
   */
  visibleSections?: ReadonlySet<ProvenanceSectionId>;
  /**
   * When true, each section renders its body directly without the outer
   * WizardAccordion wrapper. Edit-object steps already provide a Card head
   * with title/subtitle, so the inner accordion would be a redundant nesting.
   */
  flatSections?: boolean;
  /**
   * Current status of the catalog item as loaded by the parent
   * (`UploadObject.tsx`). Used to hide the "Mark as Destroyed" button on
   * items that are already `'DESTROYED'`. Passed from the parent rather
   * than pulled from `useCatalog()` because the context's `currentItem`
   * is populated asynchronously and was unreliable during initial render
   * — the parent is the source of truth since it owns the
   * `getCatalogItem` call that fetches the record.
   */
  itemStatus?: string;
}

export interface ExhibitionField {
  id: string;
  exhibition: string;
}

export interface PublicationField {
  id: string;
  text: string;
}

/**
 * Builds the persisted exhibitions/publications/awards record array from
 * wizard state, dropping rows where every user-facing field is blank.
 *
 * Why: leaving blank rows in place persists `{type: 'award', publicationText: ''}`
 * to DynamoDB, which then renders empty section headings on the detail page.
 * Shared between the debounced autosave and the unmount-flush save so both
 * paths stay in sync.
 */
export const buildExhibitionRecords = (
  exhibitionFields: ExhibitionField[],
  publications: PublicationField[],
  awards: AwardField[],
) => {
  const nonEmptyExhibitions = exhibitionFields.filter(
    (f) => f.exhibition.trim() !== '',
  );
  const nonEmptyPublications = publications.filter((p) => p.text.trim() !== '');
  const nonEmptyAwards = awards.filter((a) => a.text.trim() !== '');

  const exhibitionRecords = nonEmptyExhibitions.map((field, index) => ({
    type: 'exhibition' as const,
    order: index,
    exhibitionType: field.exhibition,
  }));
  const publicationRecords = nonEmptyPublications.map((pub, index) => ({
    type: 'publication' as const,
    order: index + nonEmptyExhibitions.length,
    publicationText: pub.text,
  }));
  const awardRecords = nonEmptyAwards.map((award, index) => ({
    type: 'award' as const,
    order: index + nonEmptyExhibitions.length + nonEmptyPublications.length,
    publicationText: award.text,
  }));

  return [...exhibitionRecords, ...publicationRecords, ...awardRecords];
};

export const ProvenanceTab = ({ catalogItemId, ownerId, itemStatus, visibleSections, flatSections }: ProvenanceTabProps) => {
  const showSection = (id: ProvenanceSectionId) =>
    !visibleSections || visibleSections.has(id);
  const renderSection = (
    args: {
      id: ProvenanceSectionId;
      title: string;
      subtitle: string;
      privacyFieldPath?: string;
      titleAdornment?: React.ReactNode;
    },
    children: React.ReactNode,
  ): React.ReactNode =>
    flatSections ? (
      <Box data-section-id={args.id}>{children}</Box>
    ) : (
      <WizardAccordion
        id={args.id}
        title={args.title}
        subtitle={args.subtitle}
        defaultExpanded={true}
        privacyFieldPath={args.privacyFieldPath}
        titleAdornment={args.titleAdornment}
      >
        {children}
      </WizardAccordion>
    );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Record Object as Destroyed toggle state (#1204 — replaced dialog with toggle)
  // Local state drives the Switch so it reflects changes immediately (#1210).
  const [isDestroyed, setIsDestroyed] = useState(() => itemStatus === 'DESTROYED');
  const [destroyLoading, setDestroyLoading] = useState(false);
  const [destroyError, setDestroyError] = useState<string | null>(null);

  // Sync local state when the parent refreshes itemStatus (e.g. background reload).
  useEffect(() => {
    setIsDestroyed(itemStatus === 'DESTROYED');
  }, [itemStatus]);

  const handleDestroyToggle = useCallback(async (checked: boolean) => {
    if (!catalogItemId) return;
    setDestroyLoading(true);
    setDestroyError(null);
    try {
      if (checked) {
        await markCatalogItemDestroyed(catalogItemId, ownerId);
        // GH#1210: stay on the provenance page so the user can toggle freely.
        // (Previously called onDestroyed?.() to navigate away — removed per #1210.)
        setIsDestroyed(true);
      } else {
        await restoreCatalogItem(catalogItemId, ownerId);
        setIsDestroyed(false);
      }
    } catch {
      setDestroyError(
        checked ? 'Failed to mark item as destroyed' : 'Failed to restore item'
      );
      // On error, leave isDestroyed unchanged (revert is implicit — state was not updated).
    } finally {
      setDestroyLoading(false);
    }
  }, [catalogItemId, ownerId]);

  // CV suggestions for exhibitions and publications autocomplete.
  // Pass ownerId so delegates see the catalog owner's exhibitions/publications (#952).
  const {
    exhibitions: rawCvExhibitions,
    publications: rawCvPublications,
    awards: rawCvAwards,
    isLoading: rawCvLoading,
    addExhibitionToCV,
    addPublicationToCV,
    addAwardToCV,
  } = useCVSuggestions(ownerId ?? undefined);

  // Commercial users don't have CVs — suppress CV suggestions
  const { isCommercialTier } = useAuthorization();
  const isCommercial = isCommercialTier();
  const cvExhibitions = isCommercial ? [] : rawCvExhibitions;
  const cvPublications = isCommercial ? [] : rawCvPublications;
  const cvAwards = isCommercial ? [] : rawCvAwards;
  const cvLoading = isCommercial ? false : rawCvLoading;

  const { currentItem, saveExhibitions, getExhibitions, getAllCatalogItems, saveAdditionalInfo, getAdditionalInfo } = useCatalog();
  const { catalogId } = useUpload();
  const effectiveCatalogId = catalogItemId || catalogId || currentItem?.catalogItemId;
  const workingCatalogId = effectiveCatalogId;

  // Show the "Mark as Destroyed" button whenever we have a catalogItemId
  // and the item is not already destroyed. Status comes from the parent
  // (`UploadObject.tsx`) which owns the `getCatalogItem` fetch — reading
  // it there avoids the async-hydration race we hit when reading from
  // `useCatalog().currentItem`.
  const {
    records,
    loadProvenanceChain,
    addRecord,
    updateRecord,
    deleteRecord,
    reorderRecords,
    saveRecords,
  } = useCatalogProvenance(effectiveCatalogId, ownerId);
  const { markStepComplete, batchUpdateTab } = useWizardCompletion();
  const { setStatus, setLastSaveTime, registerSaveNow } = useAutosaveStatus();

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const provenanceSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveRef = useRef<string>('');
  const loadedCatalogIdRef = useRef<string | null>(null);
  const recordsRef = useRef(records); // Ref to access latest records without recreating debounce
  const saveRecordsRef = useRef(saveRecords); // Ref to access latest saveRecords in cleanup
  // Publications/awards lists also start empty; users click Add Publication /
  // Add Award to surface the first row. Mirrors the exhibitions behavior.
  const [publications, setPublications] = useState<PublicationField[]>([]);
  const [awards, setAwards] = useState<AwardField[]>([]);
  const [stepNotes, setStepNotes] = useState<Record<string, string[]>>({});
  const [relatedWorks, setRelatedWorks] = useState<Array<string | { catalogItemId: string; title: string }>>([]);
  const [availableWorks, setAvailableWorks] = useState<Array<{ catalogItemId: string; label: string }>>([]);
  const relatedWorksRef = useRef(relatedWorks);
  const ownerIdRef = useRef(ownerId);

  // Lists start empty: users must click Add Exhibition to surface the first
  // input row. Avoids showing a phantom empty row that looks editable but
  // saves nothing.
  const [exhibitionFields, setExhibitionFields] = useState<ExhibitionField[]>([]);
  const [exhibitionLinkError, setExhibitionLinkError] = useState<string | null>(null);
  const [publicationLinkError, setPublicationLinkError] = useState<string | null>(null);
  const [awardLinkError, setAwardLinkError] = useState<string | null>(null);
  const [exhibitionLegacyNotice, setExhibitionLegacyNotice] = useState<string | null>(null);
  const [draggedRecordIndex, setDraggedRecordIndex] = useState<number | null>(null);
  const [draggedExhibitionIndex, setDraggedExhibitionIndex] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Autosave guards — engaged from first render so debounce/unmount-flush
  // can't fire empty defaults while loads are in flight (sibling regression
  // of DA-55L.2499). Two independent loads = two independent guards.
  const [isLoadingExhibitions, setIsLoadingExhibitions] = useState(true);
  const [isLoadingProvenanceChain, setIsLoadingProvenanceChain] = useState(true);
  const isLoadingExhibitionsRef = useRef(true);
  const isLoadingProvenanceChainRef = useRef(true);
  // Load-failure guard — DA-55L.2501 regression: when the initial GET
  // errored (axios timeout, 5xx, network drop), `.finally()` flipped
  // `isLoadingExhibitions=false` and the next debounced save read an empty
  // local state, then overwrote the persisted exhibitions[] with [].
  // This ref stays true once the load fails until a subsequent successful
  // load clears it; while true, autosave is suppressed. Refusing to save is
  // strictly safer than wiping real data — the user can retry by reopening
  // the page or by reloading.
  const exhibitionsLoadFailedRef = useRef(false);
  useEffect(() => { isLoadingExhibitionsRef.current = isLoadingExhibitions; }, [isLoadingExhibitions]);
  useEffect(() => { isLoadingProvenanceChainRef.current = isLoadingProvenanceChain; }, [isLoadingProvenanceChain]);
  const [cvSuccessMessage, setCvSuccessMessage] = useState<string | null>(null);
  const [provenanceSaveError, setProvenanceSaveError] = useState<string | null>(null);

  // Load provenance chain and exhibitions on mount or when catalogId changes
  useEffect(() => {
    // Only load if we haven't already loaded this specific catalogId
    if (workingCatalogId && loadedCatalogIdRef.current !== workingCatalogId) {
      loadedCatalogIdRef.current = workingCatalogId;
      setIsLoadingProvenanceChain(true);
      setIsLoadingExhibitions(true);
      loadProvenanceChain(workingCatalogId).catch(() => {
        // No existing provenance chain found - this is expected for new items
      }).finally(() => {
        setIsLoadingProvenanceChain(false);
      });
      getExhibitions(workingCatalogId, ownerId).then(result => {
        // Successful load — clear any prior failure flag so autosave can resume.
        exhibitionsLoadFailedRef.current = false;
        const exhibitions = result.exhibitions;
        // Legacy shim: older records stored a separate `link` attribute.
        // Safe links are composed into the display text as `[View details](url)`
        // so they render on the detail page and re-save as standard Markdown on
        // next edit. Unsafe legacy URLs (e.g. stored `javascript:` from the
        // unvalidated old field) are dropped and surfaced via an informational
        // notice so the user knows data was removed.
        let droppedLegacyLinks = 0;
        const exhibitionData = exhibitions.filter(e => e.type === 'exhibition').map(e => {
          const base = e.exhibitionType || '';
          const legacyLink = (e as { link?: string }).link;
          const hasLegacyLink = typeof legacyLink === 'string' && legacyLink.length > 0;
          const shouldAppend = hasLegacyLink && isSafeHttpUrl(legacyLink);
          if (hasLegacyLink && !shouldAppend) {
            droppedLegacyLinks += 1;
          }
          const merged = shouldAppend
            ? `${base}${base ? ' ' : ''}[View details](${legacyLink})`
            : base;
          return {
            id: crypto.randomUUID(),
            exhibition: merged
          };
        });
        if (droppedLegacyLinks > 0) {
          logger.warn('Dropped unsafe legacy exhibition links on load', { count: droppedLegacyLinks });
          setExhibitionLegacyNotice(
            `${droppedLegacyLinks} legacy exhibition link${droppedLegacyLinks === 1 ? '' : 's'} contained ` +
            `unsafe URL${droppedLegacyLinks === 1 ? '' : 's'} and ${droppedLegacyLinks === 1 ? 'was' : 'were'} removed. ` +
            `Only http:// and https:// links are supported. You can re-add links inline using [name](url) syntax.`
          );
        }
        if (exhibitionData.length > 0) {
          setExhibitionFields(exhibitionData);
        }

        const publicationData = exhibitions.filter(e => e.type === 'publication').map(e => ({
          id: crypto.randomUUID(),
          text: e.publicationText || ''
        }));
        if (publicationData.length > 0) {
          setPublications(publicationData);
        }

        const awardData = exhibitions.filter(e => e.type === 'award').map(e => ({
          id: crypto.randomUUID(),
          text: e.publicationText || ''
        }));
        if (awardData.length > 0) {
          setAwards(awardData);
        }

        // Load stepNotes from API response (migrate old per-section keys)
        if (result.stepNotes && Object.keys(result.stepNotes).length > 0) {
          setStepNotes(mergeOldNotes(result.stepNotes));
        }

        // Update completion tracking for exhibitions, publications, and awards after data loads
        const hasExhibitions = exhibitionData.some(e => e.exhibition.trim());
        const hasPublications = publicationData.some(p => p.text.trim());
        const hasAwards = awardData.some(a => a.text.trim());
        batchUpdateTab('provenance', { 1: hasExhibitions, 2: hasPublications, 3: hasAwards });

        setIsInitialized(true);
      }).catch((err) => {
        // IMPORTANT — distinguish "no existing data" (204/404 handled inside
        // getExhibitions) from "load actually failed" (timeout, 5xx, network).
        // getExhibitions only reaches this catch when the request truly
        // errored, so any error here means we have no reliable picture of
        // server state. Setting exhibitionsLoadFailedRef blocks autosave from
        // overwriting persisted data with empty defaults (DA-55L.2501).
        exhibitionsLoadFailedRef.current = true;
        logger.error('Failed to load exhibitions; suppressing autosave until reload', err);
        setIsInitialized(true);
      }).finally(() => {
        setIsLoadingExhibitions(false);
      });
    } else if (!workingCatalogId) {
      // Fresh-create flow: no id to load. Clear guards so saves can proceed
      // once an id arrives.
      setIsLoadingProvenanceChain(false);
      setIsLoadingExhibitions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingCatalogId]);

  // Lazy-load available catalog works for the Related Works dropdown.
  //
  // History: this used to fire on mount via useEffect, which meant every tab in
  // the wizard that shares this component (Provenance, Exhibitions, Publications,
  // Awards, Related Works -- 5 of the 11 tabs) eagerly issued GET /api/catalogs
  // even though the user might never open the Related Works dropdown. On a
  // dense-catalog user (2,505 items) that call took 175 s and timed out at
  // axios's 30 s ceiling, producing the "Pending → timeout" experience
  // investigated in MR !519. Most users editing an object never touch the
  // Related Works dropdown, so eager loading was pure cost.
  //
  // Now: a single ref tracks whether we've fetched, the Autocomplete's onOpen
  // triggers the fetch on first interaction, and we keep a loading flag so the
  // dropdown can show a spinner while the (still potentially large but no longer
  // 175 s after the backend Fix A) request is in flight.
  const availableWorksLoadedRef = useRef(false);
  const [availableWorksLoading, setAvailableWorksLoading] = useState(false);
  // Failure-state surface: silent-failure-hunter (post-MR-!519 review) flagged
  // that returning an empty list on error was indistinguishable from "no
  // catalog items" -- a timeout was rendering as "No matching works found".
  // We now carry an error message and show it inside the dropdown so the user
  // knows the load failed and can decide to retry by reopening.
  const [availableWorksError, setAvailableWorksError] = useState<string | null>(null);

  // Unmount guard and workingCatalogId reset in one effect.
  // ``loadAvailableWorks`` is async and the wizard re-mounts ProvenanceTab on
  // every tab switch, so the request may settle after the component is gone.
  // Calling ``setX`` on an unmounted component is a React anti-pattern
  // (warning in dev, memory leak in prod). We track mount state in a ref and
  // bail out of state updates after unmount.
  // Additionally, when the working catalog item changes, the previously loaded
  // list was filtered against the old workingCatalogId and is now stale. Reset
  // the lazy-load guard so the next dropdown open re-fetches.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    availableWorksLoadedRef.current = false;
    setAvailableWorks([]);
    setAvailableWorksError(null);
  }, [workingCatalogId]);

  const loadAvailableWorks = useCallback(async () => {
    if (availableWorksLoadedRef.current || availableWorksLoading) {
      return;
    }
    availableWorksLoadedRef.current = true;
    setAvailableWorksLoading(true);
    setAvailableWorksError(null);
    try {
      // Pass ownerId so delegates see the delegator's catalog items in the
      // Related Works dropdown, not their own empty catalog (#991).
      const catalogItems = await getAllCatalogItems(ownerId);
      if (!isMountedRef.current) return;
      const worksWithTitles = catalogItems
        .filter(item => item.catalogItemId !== workingCatalogId)
        .map((item) => {
          const title = item.title || 'Untitled';
          const identifier = item.aliCatalogNumber || item.catalogItemId.slice(-8);
          return { catalogItemId: item.catalogItemId, label: `${title} (${identifier})` };
        });
      setAvailableWorks(worksWithTitles);
    } catch (error) {
      logger.error('Failed to load available works', error);
      if (!isMountedRef.current) return;
      setAvailableWorks([]);
      setAvailableWorksError('Could not load related works. Close and reopen the dropdown to try again.');
      // Allow a retry on the next open if the first call failed -- the user
      // shouldn't be stuck with an empty dropdown forever because of a
      // transient timeout. (Deliberately NOT bounded by a max-retry count:
      // each retry requires an explicit user action -- closing and reopening
      // the dropdown -- so this is rate-limited by the user, not a runaway
      // loop. The visible error message above makes the failure obvious.)
      availableWorksLoadedRef.current = false;
    } finally {
      if (isMountedRef.current) {
        setAvailableWorksLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, workingCatalogId, getAllCatalogItems]);

  // Load existing relatedWorks from AdditionalInfo storage
  useEffect(() => {
    if (!workingCatalogId) return;
    getAdditionalInfo(workingCatalogId, ownerId).then((data) => {
      if (data.relatedWorks && data.relatedWorks.length > 0) {
        setRelatedWorks(data.relatedWorks);
      }
    }).catch(() => {
      // No existing additional info - expected for new items
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingCatalogId]);

  // Track provenance records completion (loaded via useCatalogProvenance hook)
  useEffect(() => {
    const hasRecords = records.some(r => r.publicRendering.trim() || r.internalRendering.trim());
    markStepComplete('provenance', 0, hasRecords);
  }, [records, markStepComplete]);

  // Immediate completion tracking for exhibitions, publications, and awards
  // This ensures checkmarks update immediately when user enters data, not just on save
  // Guard with isInitialized to prevent firing with empty defaults before data loads
  useEffect(() => {
    if (!isInitialized) return;
    const hasExhibitions = exhibitionFields.some(e => e.exhibition.trim());
    const hasPublications = publications.some(p => p.text.trim());
    const hasAwards = awards.some(a => a.text.trim());
    batchUpdateTab('provenance', { 1: hasExhibitions, 2: hasPublications, 3: hasAwards });
  }, [exhibitionFields, publications, awards, batchUpdateTab, isInitialized]);

  // Track Related Works completion. Related Works is step 4 of the provenance
  // tab after #1007 (awards inserted at step 3) — a non-empty string entry or
  // a linked catalog item counts. A single empty placeholder does NOT count.
  useEffect(() => {
    const hasRelatedWorks = relatedWorks.some(hasNonEmptyRelatedWork);
    markStepComplete('provenance', 4, hasRelatedWorks);
  }, [relatedWorks, markStepComplete]);

  // Smart auto-save function
  const saveExhibitionData = useCallback(async () => {
    // Guard: never save during initial load — would write empty defaults and
    // wipe the exhibitions row.
    if (!workingCatalogId || isLoadingExhibitions) return;
    // Guard: never save if the initial load failed — local state is empty
    // not because the user cleared it but because we never got the server
    // copy. Writing now would persist {} and wipe the persisted record
    // (DA-55L.2501 regression). Surface an error so the user knows.
    if (exhibitionsLoadFailedRef.current) {
      logger.warn('Suppressing exhibitions autosave: initial load failed — reload to retry');
      setStatus('error');
      setExhibitionLinkError(
        'Could not load existing exhibition data — autosave is paused to prevent data loss. ' +
        'Please reload the page; if the problem persists, contact support.'
      );
      return;
    }

    // Block save if any row contains an unsafe Markdown link. Surface the
    // inline error under the offending section and clear it when the user
    // removes the bad URL.
    const findBadRow = <T,>(rows: T[], getText: (r: T) => string): string | null => {
      for (const row of rows) {
        const result = validateMarkdownLinks(getText(row));
        if (!result.ok) return result.badUrl;
      }
      return null;
    };
    const exhBad = findBadRow(exhibitionFields, (r) => r.exhibition);
    const pubBad = findBadRow(publications, (r) => r.text);
    const awdBad = findBadRow(awards, (r) => r.text);
    setExhibitionLinkError(exhBad ? `Unsafe URL in exhibition link: ${exhBad}. Only http:// and https:// links are allowed.` : null);
    setPublicationLinkError(pubBad ? `Unsafe URL in publication link: ${pubBad}. Only http:// and https:// links are allowed.` : null);
    setAwardLinkError(awdBad ? `Unsafe URL in award link: ${awdBad}. Only http:// and https:// links are allowed.` : null);
    if (exhBad || pubBad || awdBad) {
      setStatus('error');
      return;
    }

    const allRecords = buildExhibitionRecords(exhibitionFields, publications, awards);
    const currentDataString = JSON.stringify({ records: allRecords, stepNotes });
    if (currentDataString === lastSaveRef.current) return;

    setStatus('saving');
    try {
      await saveExhibitions(workingCatalogId, allRecords, stepNotes, ownerId);
      lastSaveRef.current = currentDataString;

      // Update completion tracking for exhibitions, publications, and awards
      const hasExhibitions = exhibitionFields.some(e => e.exhibition.trim());
      const hasPublications = publications.some(p => p.text.trim());
      const hasAwards = awards.some(a => a.text.trim());
      batchUpdateTab('provenance', { 1: hasExhibitions, 2: hasPublications, 3: hasAwards });

      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (error) {
      logger.error('Failed to save exhibitions', error);
      const detail = error instanceof Error && error.message ? error.message : 'Unknown error';
      setExhibitionLinkError(`Save failed: ${detail}. Your recent changes may not have been saved — please retry.`);
      setStatus('error');
    }
  }, [workingCatalogId, isLoadingExhibitions, saveExhibitions, exhibitionFields, publications, awards, stepNotes, batchUpdateTab, setStatus, setLastSaveTime]);

  // Ref always points to the latest save function — avoids stale closure in setTimeout
  const saveExhibitionDataRef = useRef(saveExhibitionData);
  useEffect(() => { saveExhibitionDataRef.current = saveExhibitionData; }, [saveExhibitionData]);

  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveExhibitionDataRef.current(), 4000);
  }, []); // No deps - ref always has latest value

  // Provenance save function wrapped in ref for stable debounce
  const provenanceSaveFn = useCallback(() => {
    // Guard: never save during initial load — would write before the chain
    // is hydrated.
    if (isLoadingProvenanceChain) return;
    const currentRecords = recordsRef.current;
    const hasContent = currentRecords.some(r => r.publicRendering.trim() || r.internalRendering.trim());
    // Skip autosave when there are new records with empty content — the user
    // just clicked "+Add Provenance" and hasn't finished typing. Saving now
    // would filter out the empty record and the server response merge would
    // drop it from state, "kicking out" the entry (#896).
    const hasNewEmptyRecord = currentRecords.some(
      r => r.isNew && !r.publicRendering.trim() && !r.internalRendering.trim()
    );
    if (workingCatalogId && hasContent && !hasNewEmptyRecord) {
      setStatus('saving');
      saveRecords(workingCatalogId).then((success) => {
        if (success) {
          markStepComplete('provenance', 0, true);
          setStatus('saved');
          setLastSaveTime(new Date());
        } else {
          setStatus('error');
          setProvenanceSaveError('Failed to save provenance records. Your changes may not have been saved.');
          logger.error('Failed to auto-save provenance records');
        }
      }).catch((err) => {
        setStatus('error');
        setProvenanceSaveError('Failed to save provenance records. Your changes may not have been saved.');
        logger.error('Failed to auto-save provenance records', err);
      });
    }
  }, [workingCatalogId, isLoadingProvenanceChain, saveRecords, markStepComplete, setStatus, setLastSaveTime]);

  const provenanceSaveFnRef = useRef(provenanceSaveFn);
  useEffect(() => { provenanceSaveFnRef.current = provenanceSaveFn; }, [provenanceSaveFn]);

  // Debounced auto-save for provenance records — uses ref to always get latest save function
  const debouncedProvenanceSave = useCallback(() => {
    if (provenanceSaveTimeoutRef.current) clearTimeout(provenanceSaveTimeoutRef.current);
    provenanceSaveTimeoutRef.current = setTimeout(() => provenanceSaveFnRef.current(), 4000);
  }, []); // No deps - ref always has latest value

  // Save related works to AdditionalInfo storage (preserving other fields)
  const saveRelatedWorks = useCallback(async (works: Array<string | { catalogItemId: string; title: string }>) => {
    if (!workingCatalogId) return;
    try {
      const existing = await getAdditionalInfo(workingCatalogId, ownerId);
      await saveAdditionalInfo(workingCatalogId, {
        relatedWorks: works as unknown as string[],
        publicNotes: existing.publicNotes || [],
        privateNotes: existing.privateNotes || [],
        physicalLocation: existing.physicalLocation || '',
        estimatedValue: existing.estimatedValue || '',
        exhibitions: existing.exhibitions,
        stepNotes: existing.stepNotes || {},
      }, ownerId);
    } catch (err) {
      logger.error('Failed to save related works', err);
    }
  }, [workingCatalogId, getAdditionalInfo, saveAdditionalInfo]);

  // Refs to access latest data in cleanup without causing effect to re-run
  const exhibitionFieldsRef = useRef(exhibitionFields);
  const publicationsRef = useRef(publications);
  const awardsRef = useRef(awards);
  const stepNotesRef = useRef(stepNotes);
  const workingCatalogIdRef = useRef(workingCatalogId);

  // Keep refs in sync with state
  useEffect(() => {
    recordsRef.current = records;
    exhibitionFieldsRef.current = exhibitionFields;
    publicationsRef.current = publications;
    awardsRef.current = awards;
    stepNotesRef.current = stepNotes;
    workingCatalogIdRef.current = workingCatalogId;
    saveRecordsRef.current = saveRecords;
    relatedWorksRef.current = relatedWorks;
    ownerIdRef.current = ownerId;
  }, [records, exhibitionFields, publications, awards, stepNotes, workingCatalogId, saveRecords, relatedWorks, ownerId]);

  // Let the wizard "Finished" button flush this tab's pending saves before
  // navigating away. This tab runs TWO independent debounces — exhibitions/
  // publications/awards (saveExhibitionData) and provenance records
  // (provenanceSaveFn) — so it can't use the generic single-ref
  // useRegisterSaveNow wiring: that only flushed the exhibitions debounce,
  // silently dropping a provenance-record edit made in the last 4s before
  // Finish (it only reached the server later via the un-awaited unmount
  // flush below). Fixes #1238.
  useEffect(() => {
    if (typeof registerSaveNow !== 'function') return;
    registerSaveNow(async () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (provenanceSaveTimeoutRef.current) {
        clearTimeout(provenanceSaveTimeoutRef.current);
        provenanceSaveTimeoutRef.current = null;
      }

      const pendingSaves: Array<Promise<unknown>> = [saveExhibitionDataRef.current()];

      const catalogId = workingCatalogIdRef.current;
      const currentRecords = recordsRef.current;
      const hasContent = currentRecords.some(
        r => r.publicRendering.trim() || r.internalRendering.trim()
      );
      // Same guard as the debounced save and the unmount flush: never write
      // while the chain is still hydrating — versionRef is still 0, so the
      // write would fail or persist pre-hydration state.
      if (catalogId && hasContent && !isLoadingProvenanceChainRef.current) {
        pendingSaves.push(
          saveRecordsRef.current(catalogId).then((success) => {
            if (!success) {
              // saveRecords returns false without throwing (e.g. an
              // optimistic-lock conflict with an in-flight debounced save).
              // The wizard navigates away right after this flush — surface
              // the failure instead of silently dropping the edit.
              setStatus('error');
              setProvenanceSaveError(
                'Failed to save provenance records. Your changes may not have been saved.'
              );
              logger.error('Finish flush failed to save provenance records');
            }
          })
        );
      }

      await Promise.all(pendingSaves);
    });
    return () => registerSaveNow(null);
  }, [registerSaveNow, setStatus]);

  // Cleanup timeouts and flush pending saves on unmount only
  useEffect(() => {
    // Capture stable references for cleanup
    const saveTimeout = saveTimeoutRef;
    const provenanceSaveTimeout = provenanceSaveTimeoutRef;
    const records = recordsRef;

    return () => {
      // Clear any pending debounce timers
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
        saveTimeout.current = null;
      }
      if (provenanceSaveTimeout.current) {
        clearTimeout(provenanceSaveTimeout.current);
        provenanceSaveTimeout.current = null;
      }

      // Flush pending exhibition/publication saves using refs for latest data.
      // Never flush during initial load — would write empty defaults and wipe rows.
      const catalogId = workingCatalogIdRef.current;
      const stillLoading = isLoadingExhibitionsRef.current || isLoadingProvenanceChainRef.current;
      // Same load-failure guard as saveExhibitionData: if the initial GET
      // failed, the unmount flush would also wipe persisted data.
      const loadFailed = exhibitionsLoadFailedRef.current;
      if (catalogId && !stillLoading && !loadFailed) {
        const currentExhibitionFields = exhibitionFieldsRef.current;
        const currentPublications = publicationsRef.current;
        const currentAwards = awardsRef.current;

        const allRecords = buildExhibitionRecords(
          currentExhibitionFields,
          currentPublications,
          currentAwards,
        );
        const currentStepNotes = stepNotesRef.current;
        const currentDataString = JSON.stringify({ records: allRecords, stepNotes: currentStepNotes });

        // Only save if data has changed
        if (currentDataString !== lastSaveRef.current) {
          saveExhibitions(catalogId, allRecords, currentStepNotes, ownerIdRef.current).catch((err) => {
            logger.error('Failed to flush exhibition save on unmount', err);
          });
        }

        // Flush pending provenance saves using records ref for latest data
        const currentRecords = recordsRef.current;
        const hasContent = currentRecords.some(r => r.publicRendering.trim() || r.internalRendering.trim());
        if (hasContent) {
          saveRecordsRef.current(catalogId).catch((err) => {
            logger.error('Failed to flush provenance save on unmount', err);
          });
        }
      }
    };
    // Empty dependency array - only run cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Record drag handlers
  const handleRecordDragStart = (e: React.DragEvent, index: number) => {
    setDraggedRecordIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRecordDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedRecordIndex === null || draggedRecordIndex === dropIndex) return;

    reorderRecords(draggedRecordIndex, dropIndex);
    setDraggedRecordIndex(null);
    debouncedProvenanceSave();
  };

  // Exhibition handlers
  const updateExhibitionField = (index: number, field: keyof ExhibitionField, value: string) => {
    setExhibitionFields(exhibitionFields.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    ));
    debouncedSave();
  };

  const addExhibitionField = () => {
    setExhibitionFields([...exhibitionFields, { id: crypto.randomUUID(), exhibition: '' }]);
    debouncedSave();
  };

  const removeExhibitionField = (index: number) => {
    setExhibitionFields(exhibitionFields.filter((_, i) => i !== index));
    debouncedSave();
  };

  const handleExhibitionDragStart = (e: React.DragEvent, index: number) => {
    setDraggedExhibitionIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleExhibitionDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedExhibitionIndex === null || draggedExhibitionIndex === dropIndex) return;

    const newFields = [...exhibitionFields];
    const [draggedItem] = newFields.splice(draggedExhibitionIndex, 1);
    newFields.splice(dropIndex, 0, draggedItem);

    setExhibitionFields(newFields);
    setDraggedExhibitionIndex(null);
    debouncedSave();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Related works handlers
  const addRelatedWork = () => {
    const newWorks = [...relatedWorks, ''];
    setRelatedWorks(newWorks);
    saveRelatedWorks(newWorks);
  };

  const removeRelatedWork = (index: number) => {
    if (relatedWorks.length > 1) {
      const newWorks = relatedWorks.filter((_, i) => i !== index);
      setRelatedWorks(newWorks);
      saveRelatedWorks(newWorks);
    }
  };

  const updateRelatedWork = (index: number, value: string | { catalogItemId: string; title: string }) => {
    const newWorks = relatedWorks.map((w, i) => (i === index ? value : w));
    setRelatedWorks(newWorks);
    saveRelatedWorks(newWorks);
  };

  const getRelatedWorkDisplay = (work: string | { catalogItemId: string; title: string }): string => {
    if (typeof work === 'string') return work;
    return work.title || '';
  };

  // Handler for adding exhibition to CV - clears field after successful add
  const handleAddExhibitionToCV = useCallback((index: number) => async (exhibitionName: string) => {
    try {
      await addExhibitionToCV(exhibitionName, 'solo');
      // Clear the field after successful add to hide the "Add to CV" button (#688)
      const newFields = [...exhibitionFields];
      newFields[index] = { ...newFields[index], exhibition: '' };
      setExhibitionFields(newFields);
      setCvSuccessMessage(en.wizard.provenance.exhibitions.addToCVSuccess);
      debouncedSave();
    } catch (error) {
      logger.error('Failed to add exhibition to CV', error);
    }
  }, [addExhibitionToCV, exhibitionFields, debouncedSave]);

  // Handler for adding publication to CV - clears field after successful add
  const handleAddPublicationToCV = useCallback((index: number) => async (publicationTitle: string) => {
    try {
      await addPublicationToCV(publicationTitle);
      // Clear the field after successful add to hide the "Add to CV" button (#689)
      const newPubs = [...publications];
      newPubs[index] = { ...newPubs[index], text: '' };
      setPublications(newPubs);
      setCvSuccessMessage(en.wizard.provenance.publications.addToCVSuccess);
      debouncedSave();
    } catch (error) {
      logger.error('Failed to add publication to CV', error);
    }
  }, [addPublicationToCV, publications, debouncedSave]);

  // Handler for adding award to CV — issue #1007
  const handleAddAwardToCV = useCallback(async (awardText: string) => {
    try {
      await addAwardToCV(awardText);
      setCvSuccessMessage(en.wizard.provenance.awards.addToCVSuccess);
      debouncedSave();
    } catch (error) {
      logger.error('Failed to add award to CV', error);
    }
  }, [addAwardToCV, debouncedSave]);

  const handleAwardsChange = useCallback((next: AwardField[]) => {
    setAwards(next);
    debouncedSave();
  }, [debouncedSave]);

  return (
    <Box sx={{ p: 3 }}>
      <ImageThumbnail />

      {showSection('provenance-records') && renderSection(
        {
          id: 'provenance-records',
          title: en.wizard.provenance.provenance.title,
          subtitle: en.wizard.provenance.provenance.subtitle,
          privacyFieldPath: 'provenance.provenanceRecords',
        },
        (
        <Box>
          <FavoritesRail
            type="provenance"
            currentValue=""
            hideStar
            onPick={(value) => {
              addRecord(value);
              debouncedProvenanceSave();
            }}
          />
          {/* Provenance Records - All records are editable */}
          {records.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {records.map((record, index) => (
                <Box
                  key={record.id}
                  data-testid="provenance-record-row"
                  sx={{
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 1,
                    mb: 2,
                    opacity: draggedRecordIndex === index ? 0.5 : 1
                  }}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleRecordDrop(e, index)}
                >
                  <Box
                    draggable
                    onDragStart={(e) => handleRecordDragStart(e, index)}
                    onDragEnd={() => setDraggedRecordIndex(null)}
                    sx={{
                      cursor: 'grab',
                      '&:hover': { '& .MuiSvgIcon-root': { color: theme.palette.text.secondary } },
                      '&:active': { cursor: 'grabbing' }
                    }}
                  >
                    <DragIndicator sx={{ color: theme.palette.text.disabled }} />
                  </Box>
                  <TextField
                    fullWidth
                    label={`${en.wizard.provenance.provenance.publicLabel} ${index + 1}`}
                    value={record.publicRendering}
                    onChange={(e) => {
                      updateRecord(record.id, 'publicRendering', e.target.value);
                      debouncedProvenanceSave();
                    }}
                    variant="outlined"
                  />
                  <FavoriteRowToggle
                    type="provenance"
                    value={record.publicRendering}
                    ariaLabel={`Save provenance ${index + 1} as favorite`}
                  />
                  <TextField
                    fullWidth
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Always private - never shared publicly">
                          <LockIcon sx={{ fontSize: 16, color: '#800000' }} />
                        </Tooltip>
                        {`${en.wizard.provenance.provenance.internalLabel} ${index + 1}`}
                      </Box>
                    }
                    value={record.internalRendering}
                    onChange={(e) => {
                      updateRecord(record.id, 'internalRendering', e.target.value);
                      debouncedProvenanceSave();
                    }}
                    variant="outlined"
                  />
                  <IconButton
                    onClick={() => {
                      deleteRecord(record.id);
                      debouncedProvenanceSave();
                    }}
                    color="error"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              startIcon={<Add />}
              onClick={() => addRecord()}
              variant="outlined"
              size="small"
            >
              {en.wizard.provenance.provenance.addButton}
            </Button>
            {!!catalogItemId && (
              <FormControlLabel
                sx={{ ml: 'auto' }}
                control={
                  <Switch
                    checked={isDestroyed}
                    onChange={(e) => { void handleDestroyToggle(e.target.checked); }}
                    disabled={destroyLoading}
                    color="warning"
                  />
                }
                label="Record Object as Destroyed"
              />
            )}
          </Box>
        </Box>
        ),
      )}

      {showSection('provenance-exhibitions') && renderSection(
        {
          id: 'provenance-exhibitions',
          title: en.wizard.provenance.exhibitions.title,
          subtitle: en.wizard.provenance.exhibitions.subtitle,
          privacyFieldPath: 'provenance.exhibitions',
          titleAdornment: <MarkdownLinkHelp />,
        },
        (
        <Box>
          {exhibitionLegacyNotice && (
            <Alert
              severity="warning"
              onClose={() => setExhibitionLegacyNotice(null)}
              sx={{ mb: 2 }}
            >
              {exhibitionLegacyNotice}
            </Alert>
          )}
          {exhibitionLinkError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {exhibitionLinkError}
            </Alert>
          )}
          <FavoritesRail
            type="exhibitions"
            currentValue=""
            hideStar
            onPick={(value) => {
              setExhibitionFields([...exhibitionFields, { id: crypto.randomUUID(), exhibition: value }]);
              debouncedSave();
            }}
          />
          {exhibitionFields.map((field, index) => (
            <Box
              key={field.id}
              data-testid="exhibition-row"
              sx={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1,
                mb: 2,
                opacity: draggedExhibitionIndex === index ? 0.5 : 1
              }}
              onDragOver={handleDragOver}
              onDrop={(e) => handleExhibitionDrop(e, index)}
            >
              <Box
                draggable
                onDragStart={(e) => handleExhibitionDragStart(e, index)}
                onDragEnd={() => setDraggedExhibitionIndex(null)}
                sx={{
                  cursor: 'grab',
                  '&:hover': { '& .MuiSvgIcon-root': { color: theme.palette.text.secondary } },
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <DragIndicator sx={{ color: theme.palette.text.disabled }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CVAutocomplete
                  label={`${en.wizard.provenance.exhibitions.exhibitionLabel} ${index + 1}`}
                  value={field.exhibition}
                  options={cvExhibitions}
                  onChange={(value) => updateExhibitionField(index, 'exhibition', value)}
                  onAddToCV={isCommercial ? undefined : handleAddExhibitionToCV(index)}
                  placeholder={en.wizard.provenance.exhibitions.placeholder}
                  isLoading={cvLoading}
                />
              </Box>
              <FavoriteRowToggle
                type="exhibitions"
                value={field.exhibition}
                ariaLabel={`Save exhibition ${index + 1} as favorite`}
              />
              <IconButton
                onClick={() => removeExhibitionField(index)}
                color="error"
                size="small"
                aria-label={`Remove exhibition ${index + 1}`}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<Add />}
            onClick={addExhibitionField}
            variant="outlined"
            size="small"
          >
            {en.wizard.provenance.exhibitions.addButton}
          </Button>
        </Box>
        ),
      )}

      {showSection('provenance-publications') && renderSection(
        {
          id: 'provenance-publications',
          title: en.wizard.provenance.publications.title,
          subtitle: en.wizard.provenance.publications.subtitle,
          privacyFieldPath: 'provenance.publications',
          titleAdornment: <MarkdownLinkHelp />,
        },
        (
        <Box>
          {publicationLinkError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {publicationLinkError}
            </Alert>
          )}
          <FavoritesRail
            type="publications"
            currentValue=""
            hideStar
            onPick={(value) => {
              setPublications([...publications, { id: crypto.randomUUID(), text: value }]);
              debouncedSave();
            }}
          />
          {publications.map((pub, index) => (
            <Box
              key={pub.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2
              }}
            >
              <CVAutocomplete
                label={`${en.wizard.provenance.publications.label} ${index + 1}`}
                value={pub.text}
                options={cvPublications}
                onChange={(value) => {
                  const newPubs = [...publications];
                  newPubs[index] = { ...pub, text: value };
                  setPublications(newPubs);
                  debouncedSave();
                }}
                onAddToCV={isCommercial ? undefined : handleAddPublicationToCV(index)}
                placeholder={en.wizard.provenance.publications.placeholder}
                isLoading={cvLoading}
              />
              <FavoriteRowToggle
                type="publications"
                value={pub.text}
                ariaLabel={`Save publication ${index + 1} as favorite`}
              />
              <IconButton
                onClick={() => {
                  setPublications(publications.filter((_, i) => i !== index));
                  debouncedSave();
                }}
                color="error"
                size="small"
                aria-label={`Remove publication ${index + 1}`}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<Add />}
            onClick={() => {
              setPublications([...publications, { id: crypto.randomUUID(), text: '' }]);
              debouncedSave();
            }}
            variant="outlined"
            size="small"
          >
            {en.wizard.provenance.publications.addButton}
          </Button>
        </Box>
        ),
      )}

      {showSection('provenance-awards') && (
      <AwardsSection
        awards={awards}
        onChange={handleAwardsChange}
        cvAwards={cvAwards}
        cvLoading={cvLoading}
        onAddToCV={isCommercial ? undefined : handleAddAwardToCV}
        linkError={awardLinkError}
        flat={flatSections}
      />
      )}

      {showSection('provenance-related-works') && renderSection(
        {
          id: 'provenance-related-works',
          title: en.wizard.provenance.relatedWorks.title,
          subtitle: en.wizard.provenance.relatedWorks.subtitle,
          privacyFieldPath: 'additionalInfo.relatedWorks',
        },
        (
        <Box>
          {relatedWorks.map((work, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {typeof work === 'object' && work !== null && 'catalogItemId' in work && (
                <Tooltip title="Linked — reciprocal link exists on the related work">
                  <LinkIcon
                    data-testid="reciprocal-link-indicator"
                    fontSize="small"
                    color="primary"
                    sx={{ opacity: 0.7, flexShrink: 0 }}
                  />
                </Tooltip>
              )}
              <Autocomplete
                fullWidth
                options={availableWorks}
                loading={availableWorksLoading}
                onOpen={loadAvailableWorks}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.label;
                }}
                value={null}
                inputValue={getRelatedWorkDisplay(work)}
                onInputChange={(_event, newInputValue, reason) => {
                  if (reason === 'input') {
                    updateRelatedWork(index, newInputValue);
                  }
                }}
                onChange={(_event, newValue) => {
                  if (newValue && typeof newValue === 'object' && 'catalogItemId' in newValue) {
                    updateRelatedWork(index, { catalogItemId: newValue.catalogItemId, title: newValue.label });
                  } else {
                    updateRelatedWork(index, '');
                  }
                }}
                isOptionEqualToValue={() => false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${en.wizard.provenance.relatedWorks.label} ${index + 1}`}
                    variant="outlined"
                    placeholder={en.wizard.provenance.relatedWorks.placeholder}
                    // Surface the lazy-load failure as helperText so the user
                    // sees it without having to re-open the dropdown. Falls
                    // back to a normal "no matches" message inside the
                    // listbox when there is no error.
                    error={!!availableWorksError}
                    helperText={availableWorksError}
                  />
                )}
                noOptionsText={en.wizard.provenance.relatedWorks.noMatchesText}
                freeSolo
              />
              {typeof work === 'object' && work !== null && 'catalogItemId' in work && (
                <RelatedWorkThumbnail
                  catalogItemId={work.catalogItemId}
                  title={work.title}
                  ownerId={ownerId}
                />
              )}
              {relatedWorks.length > 1 && (
                <IconButton
                  onClick={() => removeRelatedWork(index)}
                  color="error"
                  size="small"
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            startIcon={<Add />}
            onClick={addRelatedWork}
            variant="outlined"
            size="small"
          >
            {en.wizard.provenance.relatedWorks.addButton}
          </Button>
        </Box>
        ),
      )}

      {/* CV Success Snackbar */}
      <Snackbar
        open={!!cvSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setCvSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setCvSuccessMessage(null)}
          sx={{ width: '100%' }}
        >
          {cvSuccessMessage}
        </Alert>
      </Snackbar>

      {/* Provenance Save Error Snackbar */}
      <Snackbar
        open={!!provenanceSaveError}
        autoHideDuration={6000}
        onClose={() => setProvenanceSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setProvenanceSaveError(null)}
          sx={{ width: '100%' }}
        >
          {provenanceSaveError}
        </Alert>
      </Snackbar>

      {/* Record-as-Destroyed Error Snackbar (#1204 — the removed confirm dialog
          used to surface this; keep the failure visible to the user) */}
      <Snackbar
        open={!!destroyError}
        autoHideDuration={6000}
        onClose={() => setDestroyError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setDestroyError(null)}
          sx={{ width: '100%' }}
        >
          {destroyError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
